import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, MenuItem, TextField } from "@material-ui/core";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { Link as RouterLink } from "react-router-dom";
import { planEatApi } from "../../../planEatApi/PlanEatApi";
import { ICompanyContent, IEaterGroupContent, IOfficeContent } from "../../../planEatApi/PlanEatFacade";
import { prepareEaterGroupAdmins } from "../pages/User/saveAccount";
import { useSysadminData } from "../SysadminData";
import { errorMessage, SysadminSection, sysadminPaths } from "../sysadminUtils";
import { AdminPicker } from "./AdminPicker";

export type EditTarget =
    | { kind: "company"; id?: string }
    | { kind: "office"; id?: string; companyId?: string }
    | { kind: "eaterGroup"; id?: string; officeId?: string };

interface Field {
    key: string;
    label: string;
    required?: boolean;
    width?: 4 | 6 | 12;
    options?: { value: string; label: string }[];
}

interface Form {
    values: Record<string, string>;
    admins: string[];
}

const labels: Record<EditTarget["kind"], string> = { company: "company", office: "office", eaterGroup: "eater group" };
const sections: Record<EditTarget["kind"], SysadminSection> = { company: "companies", office: "offices", eaterGroup: "eaterGroups" };

const emptyCompany: ICompanyContent = {
    id: "", name: "", description: "", companyName: "", companyCvr: "", companyAddress: "", companyPhoneNumber: "", companyAdminIds: [],
};
const emptyOffice: IOfficeContent = {
    id: "", name: "", description: "", companyId: "", officeName: "", officeAddress: "", officeAdminIds: [],
    defaultSeatings: [], defaultAvailability: [], defaultDishesContent: [], officeSettings: undefined,
};
const emptyEaterGroup: IEaterGroupContent = {
    id: "", name: "", description: "", officeId: "", eaterGroupName: "", eaterGroupCVR: "", eaterGroupAddress: "", eaterGroupPhoneNumber: "", eaterGroupAdminIds: [],
};

/** Create or edit a company, office or eater group without leaving the page. */
export const EntityDialog: React.FC<{ target: EditTarget; onClose: () => void; onSaved?: (id: string) => void; pageLink?: boolean }> = ({
    target, onClose, onSaved, pageLink = true,
}) => {
    const data = useSysadminData();
    const [saving, setSaving] = useState(false);
    const isNew = !target.id;
    const label = labels[target.kind];

    const [form, setForm] = useState<Form>((): Form => {
        if (target.kind === "company") {
            const c = data.companyById(target.id) ?? emptyCompany;
            return { values: { companyName: c.companyName, companyCvr: c.companyCvr, companyAddress: c.companyAddress, companyPhoneNumber: c.companyPhoneNumber }, admins: c.companyAdminIds ?? [] };
        }
        if (target.kind === "office") {
            const o = data.officeById(target.id) ?? { ...emptyOffice, companyId: target.companyId ?? data.selectedCompanyId ?? "" };
            return { values: { officeName: o.officeName, officeAddress: o.officeAddress, companyId: o.companyId }, admins: o.officeAdminIds ?? [] };
        }
        // A new eater group goes in the selected company's office when it only has one
        const companyOffices = data.offices.filter(o => data.selectedCompanyId && o.content.companyId === data.selectedCompanyId);
        const defaultOffice = target.officeId ?? (companyOffices.length === 1 ? companyOffices[0].content.id : "");
        const g = data.eaterGroupById(target.id) ?? { ...emptyEaterGroup, officeId: defaultOffice };
        return {
            values: { eaterGroupName: g.eaterGroupName, officeId: g.officeId, eaterGroupCVR: g.eaterGroupCVR, eaterGroupPhoneNumber: g.eaterGroupPhoneNumber, eaterGroupAddress: g.eaterGroupAddress },
            admins: g.eaterGroupAdminIds ?? [],
        };
    });

    const byLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);
    const companyOptions = data.companies.map(c => ({ value: c.content.id, label: c.content.companyName || "Unnamed company" })).sort(byLabel);
    const officeOptions = data.offices
        .filter(o => data.matchesSelectedCompany(o.content.companyId) || o.content.id === form.values.officeId)
        .map(o => ({ value: o.content.id, label: `${o.content.officeName || "Unnamed office"} (${data.companyById(o.content.companyId)?.companyName ?? "Unknown company"})` }))
        .sort(byLabel);

    const fields: Field[] = target.kind === "company" ? [
        { key: "companyName", label: "Name", required: true, width: 6 },
        { key: "companyCvr", label: "CVR", width: 6 },
        { key: "companyAddress", label: "Address", width: 6 },
        { key: "companyPhoneNumber", label: "Phone number", width: 6 },
    ] : target.kind === "office" ? [
        { key: "officeName", label: "Name", required: true, width: 6 },
        { key: "companyId", label: "Company", required: true, width: 6, options: companyOptions },
        { key: "officeAddress", label: "Address", width: 12 },
    ] : [
        { key: "eaterGroupName", label: "Name", required: true, width: 6 },
        { key: "officeId", label: "Office", required: true, width: 6, options: officeOptions },
        { key: "eaterGroupCVR", label: "CVR", width: 4 },
        { key: "eaterGroupPhoneNumber", label: "Phone number", width: 4 },
        { key: "eaterGroupAddress", label: "Address", width: 4 },
    ];

    const missing = fields.filter(f => f.required && !(form.values[f.key] ?? "").trim()).map(f => f.label.toLowerCase());
    if (form.admins.length === 0) missing.push("at least one admin");

    async function save() {
        setSaving(true);
        try {
            let savedId: string;
            if (target.kind === "company") {
                const latest = target.id ? (await planEatApi.getCompany(target.id)).data.content : emptyCompany;
                savedId = (await planEatApi.postCompany({ ...latest, ...form.values, companyAdminIds: form.admins } as ICompanyContent)).data.content.id;
            } else if (target.kind === "office") {
                const latest = target.id ? (await planEatApi.getOffice(target.id)).data.content : emptyOffice;
                savedId = (await planEatApi.postOffice({ ...latest, ...form.values, officeAdminIds: form.admins } as IOfficeContent)).data.content.id;
            } else {
                const latest = target.id ? (await planEatApi.getEaterGroup(target.id)).data.content : emptyEaterGroup;
                // Avoids the backend storing the admins' user groups in a broken format
                await prepareEaterGroupAdmins(form.admins);
                savedId = (await planEatApi.postEaterGroup({ ...latest, ...form.values, eaterGroupAdminIds: form.admins } as IEaterGroupContent)).data.content.id;
            }
            toast.success(`${label[0].toUpperCase() + label.slice(1)} ${isNew ? "created" : "saved"}`);
            await data.reload();
            onSaved?.(savedId);
            onClose();
        } catch (e) {
            toast.error(errorMessage(e, `Could not save ${label}`));
            setSaving(false);
        }
    }

    const setValue = (key: string, value: string) => setForm(f => ({ ...f, values: { ...f.values, [key]: value } }));

    return (
        <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isNew ? `New ${label}` : `Edit ${label}`}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {fields.map(field => (
                        <Grid item xs={12} sm={field.width ?? 6} key={field.key}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label={field.label}
                                required={field.required}
                                select={!!field.options}
                                autoFocus={field === fields[0]}
                                value={form.values[field.key] ?? ""}
                                onChange={e => setValue(field.key, e.target.value)}
                            >
                                {field.options?.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <AdminPicker value={form.admins} onChange={admins => setForm(f => ({ ...f, admins }))} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                {target.id && pageLink && (
                    <Link component={RouterLink} to={sysadminPaths.detail(sections[target.kind], target.id)} variant="body2" style={{ marginRight: "auto", marginLeft: 16 }}>
                        History and delete
                    </Link>
                )}
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" color="primary" disabled={saving || missing.length > 0} onClick={save}
                    title={missing.length ? `Missing ${missing.join(", ")}` : undefined}>
                    {saving ? "Saving…" : isNew ? `Create ${label}` : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
